import { CommonModule, I18nPluralPipe } from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import {
    APP_INITIALIZER,
    CUSTOM_ELEMENTS_SCHEMA,
    ErrorHandler,
    inject,
    Injector,
    NgModule,
    NO_ERRORS_SCHEMA,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { BannerModule } from '@vg-constellation/angular-20/banner';
import { ButtonModule } from '@vg-constellation/angular-20/button';
import { CheckboxModule } from '@vg-constellation/angular-20/checkbox';
import { ChipModule } from '@vg-constellation/angular-20/chip';
import { DetailsModule } from '@vg-constellation/angular-20/details';
import { DrawerModule } from '@vg-constellation/angular-20/drawer';
import { HintErrorModule } from '@vg-constellation/angular-20/hint-error';
import { IconModule } from '@vg-constellation/angular-20/icon';
import { InputModule } from '@vg-constellation/angular-20/input';
import { DropdownModule } from '@vg-constellation/angular-20/dropdown';
import { SelectModule } from '@vg-constellation/angular-20/select';
import { InternalCalloutModule } from '@vg-constellation/angular-20/internal-callout';
import { LabelModule } from '@vg-constellation/angular-20/label';
import { LinkModule } from '@vg-constellation/angular-20/link';
import { ModalDialogModule } from '@vg-constellation/angular-20/modal-dialog';
import { NavOverflowModule } from '@vg-constellation/angular-20/nav-overflow';
import { NavigationModule } from '@vg-constellation/angular-20/navigation';
import { RadioModule } from '@vg-constellation/angular-20/radio';
import { SpinnerModule } from '@vg-constellation/angular-20/spinner';
import { StepIndicatorModule } from '@vg-constellation/angular-20/step-indicator';
import { TooltipModule } from '@vg-constellation/angular-20/tooltip';
import { IMaskModule } from 'angular-imask';
import { CookieService } from 'ngx-cookie-service';

import { environment } from '../environments/environment';
import { AppMultiAccountComponent } from './app-multi-account.component';
import { AppMultiAccountRoutingModule } from './app-multi-account-routing.module';
import { AccessComparisonComponent } from './components/access-comparison/access-comparison.component';
import { ActionMenuWrapperComponent } from './components/action-menu-wrapper/action-menu-wrapper.component';
import { ActionNavOverflowMenuComponent } from './components/action-nav-overflow-menu/action-nav-overflow-menu.component';
import { BackLinkComponent } from './components/back-link/back-link.component';
import { DynamicRouteComponent } from './components/dynamic-route/dynamic-route.component';
import { InterstitialPageContentComponent } from './components/interstitial-page-content/interstitial-page-content.component';
import { LoadingIndicatorComponent } from './components/loading-indicator/loading-indicator.component';
import { AboutPageComponent } from './components/pages/about-page/about-page.component';
import { AccountPermissionsPageComponent } from './components/pages/account-permissions-page/account-permissions-page.component';
import { IneligiblePageComponent } from './components/pages/ineligible-page/ineligible-page.component';
import { AddUsersPageComponent } from './components/pages/interstitial-pages/accounts-interstitial-pages/add-users-page/add-users-page.component';
import { EditUsersPageComponent } from './components/pages/interstitial-pages/accounts-interstitial-pages/edit-users-page/edit-users-page.component';
import { RemoveFromAccountPageComponent } from './components/pages/interstitial-pages/accounts-interstitial-pages/remove-from-account-page/remove-from-account-page.component';
import { RemoveUsersPageComponent } from './components/pages/interstitial-pages/accounts-interstitial-pages/remove-users-page/remove-users-page.component';
import { AddExistingUserPageComponent } from './components/pages/interstitial-pages/users-interstitial-pages/add-existing-user-page/add-existing-user-page.component';
import { EditUserAccessPageComponent } from './components/pages/interstitial-pages/users-interstitial-pages/edit-user-access-page/edit-user-access-page.component';
import { RemoveUserAccessPageComponent } from './components/pages/interstitial-pages/users-interstitial-pages/remove-user-access-page/remove-user-access-page.component';
import { RemoveUserConfirmationPageComponent } from './components/pages/remove-user-confirmation-page/remove-user-confirmation-page.component';
import { RemoveUserFromAccountPageComponent } from './components/pages/remove-user-from-account-page/remove-user-from-account-page.component';
import { TechnicalDifficultiesPageComponent } from './components/pages/technical-difficulties-page/technical-difficulties-page.component';
import { UnsuccessfulAttemptPageComponent } from './components/pages/unsuccessful-attempt-page/unsuccessful-attempt-page.component';
import { UsersOnAccountPageComponent } from './components/pages/users-on-account-page/users-on-account-page.component';
import { PendingChipComponent } from './components/pending-chip/pending-chip.component';
import { AdvisorComponent } from './components/representative-card/advisor/advisor.component';
import { RepresentativeCardComponent } from './components/representative-card/representative-card.component';
import { SearchFieldComponent } from './components/search-field/search-field.component';
import { RafReviewStepComponent } from './components/stepper-steps/remove-access-flow/raf-review-step/raf-review-step.component';
import { RafSelectUserStepComponent } from './components/stepper-steps/remove-access-flow/raf-select-user-step/raf-select-user-step.component';
import { TableComponent } from './components/table/table.component';
import { ObservableAccountsComponent } from './components/tabs/observable-accounts/observable-accounts.component';
import { OwnAccountsComponent } from './components/tabs/own-accounts/own-accounts.component';
import { UsersComponent } from './components/tabs/users/users.component';
import { UsersOnAccountComponent } from './components/tabs/users-on-account/users-on-account.component';
import { ObservableAccountTileComponent } from './components/tiles/observable-account-tile/observable-account-tile.component';
import { OwnAccountTileComponent } from './components/tiles/own-account-tile/own-account-tile.component';
import { RmInfoTileComponent } from './components/tiles/rm-info-tile/rm-info-tile.component';
import { SingleStatusListTileComponent } from './components/tiles/single-status-list-tile/single-status-list-tile.component';
import { TileComponent } from './components/tiles/tile.component';
import { TriageTileComponent } from './components/tiles/triage-tile/triage-tile.component';
import { UserTileComponent } from './components/tiles/user-tile/user-tile.component';
import { UserCountComponent } from './components/user-count/user-count.component';
import { ViewSelectorComponent } from './components/view-selector/view-selector.component';
import { AutofocusDirective } from './directives/autofocus.directive';
import { CustomCellDirective } from './directives/custom-cell.directive';
import { KeyboardEventRegexValidateDirective } from './directives/keyboard-event-regex-validate.directive';
import { LoadingDirective } from './directives/loading.directive';
import { AscDescLabelPipe } from './pipes/asc-desc-label/asc-desc-label.pipe';
import { MapAccountTypePipe } from './pipes/map-account-type/map-account-type.pipe';
import { ReplaceParamPipe } from './pipes/replace-param/replace-param.pipe';
import { MapRoleTypePipe } from './pipes/role-type/map-role-type.pipe';
import { ContentService } from './services/content/content.service';
import { AppGlobalErrorHandler } from './services/error-hadler/global-error.handler';
import { AccountPermissionStoreModule } from './store/account-permissions-store.module';
import { initializeApp } from './app-initializer';
import { HttpRequestInterceptor } from './interceptors/with-credentials-interceptor';
import { AuthRedirectInterceptor } from './interceptors/auth-redirect.interceptor';
import { StepperComponent } from './components/stepper/stepper.component';
import { GafConfirmationPageMvpComponent } from './components/pages/gaf-confirmation-page-mvp/gaf-confirmation-page-component-mvp';
import { GafMvpPinComponent } from './components/stepper-steps/grant-access-flow-mvp/gaf-mvp-pin-component/gaf-mvp-pin-component';
import { AddUserToAccountPageMvpComponent } from './components/pages/add-user-to-account-page-mvp/add-user-to-account-page-mvp.component';
import { RafSelectAccountsStepComponent } from './components/stepper-steps/remove-access-flow/raf-select-accounts-step/raf-select-accounts-step.component';
import {
    ExistingGranteeSuccessPageComponent,
    NewGranteeInfoOnlySuccessPageComponent,
    NewGranteeLimitedSuccessPageComponent,
    NvcInfoOnlySuccessPageComponent,
    NvcLimitedSuccessPageComponent,
} from './components/stepper-steps/grantee-flow/grantee-success-page/grantee-success-page.component';
import { GranteePinInputComponent } from './components/stepper-steps/grantee-flow/grantee-pin-input/grantee-pin-input.component';
import { GranteeLandingPageComponent } from './components/pages/grantee-landing-page/grantee-landing-page.component';
import { GenericErrorComponent } from './components/pages/generic-error/generic-error.component';
import { GranteeWebRegPageComponent } from './components/pages/grantee-web-reg-page/grantee-web-reg-page.component';
import { GranteeWelcomePageComponent } from './components/stepper-steps/grantee-flow/grantee-welcome-page/grantee-welcome-page.component';
import { SecureModalComponent } from './components/secure-modal/secure-modal.component';
import { LightRegNavTopComponent } from './components/light-reg-nav-top/light-reg-nav-top.component';
import { LightRegNavBottomComponent } from './components/light-reg-nav-bottom/light-reg-nav-bottom.component';
import { AlmostThereComponent } from './components/stepper-steps/grantee-flow/almost-there/almost-there.component';
import { RemoveFromAccountPagePIComponent } from './components/pages/interstitial-pages/accounts-interstitial-pages/remove-from-account-page-pi/remove-from-account-page-pi.component';
import { EditUserAccessPagePIComponent } from './components/pages/interstitial-pages/users-interstitial-pages/edit-user-access-page-pi/edit-user-access-page-pi.component';
import { LoggerService } from './services/logger/logger.service';

@NgModule({
    declarations: [],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    exports: [],
    imports: [
        CommonModule,
        AppMultiAccountRoutingModule,
        LinkModule,
        IconModule,
        NavigationModule,
        NavOverflowModule,
        CheckboxModule,
        InputModule,
        DropdownModule,
        SelectModule,
        ReactiveFormsModule,
        ButtonModule,
        SpinnerModule,
        StepIndicatorModule,
        AccountPermissionStoreModule,
        ReplaceParamPipe,
        StoreDevtoolsModule.instrument({
            maxAge: 25,
            logOnly: environment.development,
            autoPause: true,
            connectInZone: true,
        }),
        FormsModule,
        RadioModule,
        ReactiveFormsModule,
        BannerModule,
        DetailsModule,
        LabelModule,
        HintErrorModule,
        TooltipModule,
        IMaskModule,
        DrawerModule,
        ModalDialogModule.forRoot(),
        ChipModule,
        InternalCalloutModule,
        MapRoleTypePipe,
        MapAccountTypePipe,
        AscDescLabelPipe,
        LoadingDirective,
        AppMultiAccountComponent,
        AccessComparisonComponent,
        ActionMenuWrapperComponent,
        ActionNavOverflowMenuComponent,
        BackLinkComponent,
        DynamicRouteComponent,
        InterstitialPageContentComponent,
        LightRegNavBottomComponent,
        LightRegNavTopComponent,
        LoadingIndicatorComponent,
        AboutPageComponent,
        AccountPermissionsPageComponent,
        AddUserToAccountPageMvpComponent,
        RafSelectAccountsStepComponent,
        GafConfirmationPageMvpComponent,
        GenericErrorComponent,
        GranteeLandingPageComponent,
        GranteeWebRegPageComponent,
        IneligiblePageComponent,
        AddUsersPageComponent,
        StepperComponent,
        AdvisorComponent,
        GafMvpPinComponent,
        TileComponent,
        PendingChipComponent,
        RmInfoTileComponent,
        SingleStatusListTileComponent,
        CustomCellDirective,
        AutofocusDirective,
        UserCountComponent,
        ViewSelectorComponent,
        UserTileComponent,
        KeyboardEventRegexValidateDirective,
        TriageTileComponent,
        UsersOnAccountComponent,
        TableComponent,
        UsersComponent,
        ObservableAccountsComponent,
        NvcLimitedSuccessPageComponent,
        NvcInfoOnlySuccessPageComponent,
        AlmostThereComponent,
        SecureModalComponent,
        GranteeWelcomePageComponent,
        GranteePinInputComponent,
        ExistingGranteeSuccessPageComponent,
        NewGranteeInfoOnlySuccessPageComponent,
        NewGranteeLimitedSuccessPageComponent,
        RafSelectUserStepComponent,
        RafReviewStepComponent,
        RemoveUserConfirmationPageComponent,
        RemoveUserFromAccountPageComponent,
        TechnicalDifficultiesPageComponent,
        UnsuccessfulAttemptPageComponent,
        RepresentativeCardComponent,
        RemoveFromAccountPageComponent,
        RemoveFromAccountPagePIComponent,
        AddExistingUserPageComponent,
        EditUserAccessPageComponent,
        EditUserAccessPagePIComponent,
        RemoveUserAccessPageComponent,
        RemoveUsersPageComponent,
        OwnAccountsComponent,
        EditUsersPageComponent,
        SearchFieldComponent,
        UsersOnAccountPageComponent,
        ObservableAccountTileComponent,
        OwnAccountTileComponent,
    ],
    providers: [
        ContentService,
        { provide: ErrorHandler, useClass: AppGlobalErrorHandler },
        { provide: Window, useValue: window },
        I18nPluralPipe,
        MapRoleTypePipe,
        MapAccountTypePipe,
        CookieService,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            multi: true,
            deps: [HttpClient, Window, LoggerService],
        },
        { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AuthRedirectInterceptor, multi: true },
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppMultiAccountModule {
    static injector: Injector;

    constructor() {
        AppMultiAccountModule.injector = inject(Injector);
    }
}
